//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { FullStory, init as initFullStory } from '@fullstory/browser';
import * as Sentry from '@sentry/browser';
import { fullStoryIntegration } from '@sentry/fullstory';

import { isProdBuild } from '@utils/envUtils';
import { isNullOrEmpty } from '@utils/stringUtils';

import { ICuralateSession } from 'app/basic-session/ICuralateSession';
import { IPublicLogger } from 'app/logger/loggerModel';

export const LOGGER_LOCAL_SESSION_WARNING =
  'Running locally, logger sessions will not be recorded';

export class Logger {
  private initCalled = false;
  private identifyCalled = false;
  public cloud: IPublicLogger;
  public console: Console;

  // Anything you want sent directly to cloud logging services (without touching the console)
  // should use the `cloud` property. e.g. logger.cloud.warn('fake warning');
  constructor() {
    this.console = console;
    this.cloud = {
      error: (...params) => {
        if (this.isInitialized()) {
          FullStory('log', { level: 'error', msg: JSON.stringify(params) });

          if (params[0] instanceof Error) {
            Sentry.captureException(params[0]);
          } else {
            Sentry.captureException(new Error(JSON.stringify(params)));
          }
        }
      },
      warn: (...params) => {
        if (this.isInitialized()) {
          FullStory('log', { level: 'warn', msg: JSON.stringify(params) });
        }
      },
      log: (...params) => {
        if (this.isInitialized()) {
          FullStory('log', { level: 'log', msg: JSON.stringify(params) });
        }
      },
      captureException: (e) => {
        if (this.isInitialized()) {
          Sentry.captureException(e);
        }
      },
      getCurrentSessionUrl: () =>
        this.isInitialized() ? (FullStory('getSession', { format: 'url' }) as string | null) ?? null : null,
    };
  }

  public isInitialized() {
    return this.initCalled;
  }

  public isIdentified() {
    return this.identifyCalled;
  }

  public init(release?: string, hostname?: string) {
    if (this.isInitialized()) {
      return;
    }
    // This conditional is used to prevent local instances of the dashboard
    // from having session information sent to logging services.
    // This lets us not load fullstory and sentry for the screen-capture-service.
    if (isProdBuild(release, hostname, navigator.webdriver)) {
      initFullStory({
        orgId: 'ZFBHF',
      });
      Sentry.init({
        dsn: 'https://ce388f49a553402ea4ff794d8fcc49e0@o34336.ingest.sentry.io/5635156',
        integrations: [
          fullStoryIntegration('bazaarvoice-inc', { client: FullStory }),
          Sentry.browserTracingIntegration(),
        ],

        // Configures the percentage of transactions that are sampled
        tracesSampleRate: 0.1,

        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        // Setting to none for now because we need all apis called to allow `sentry-trace` and `baggage` headers
        tracePropagationTargets: [],
      });
      this.initCalled = true;
    } else {
      this.console.log(LOGGER_LOCAL_SESSION_WARNING);
    }
  }

  public identify(session: ICuralateSession, isProductionMode: boolean) {
    if (this.isIdentified()) {
      return;
    }

    // Similar to the check we have in the init() method above, this check
    // is used to determine whether or not the session is coming from a
    // local instance of the dashboard. If it is, we do not want to send
    // information to logging services. This check is more reliable than the earlier
    // one because it uses information from the /api/session request.
    //
    // Note: The reason we do not use this check for the init() call is because
    // we want to record production sessions to logging services even if the /api/session
    // request fails.
    if (!isProductionMode || !this.isInitialized()) {
      return;
    }

    const identificationData = {
      crl8_id: session.user.id,
      name: session.user.email,
      email: session.user.email,
      dd: 'dashboard-frontend',
      // InstanceId is not populated yet for Legacy Webapp service,
      // So let's add a fallback value to signify that this is a known case.
      instanceId: session.instanceId ?? 'lws-generic',
      clientId: session.client.id,
      environment: session.isProduction ? 'curalate-prod' : 'curalate-qa',
    };

    if (isNullOrEmpty(session.user.portalGuid)) {
      // Prefix the userIds fed into FullStory to avoid
      // confusion with other existing userId fields in the BV system
      FullStory('setIdentity', { uid: `crl8_${session.user.id}`, properties : identificationData });
    } else {
      FullStory('setIdentity', { uid: session.user.portalGuid, properties : identificationData });

    }

    Sentry.setUser({
      current_client_id: session.client.id.toString(),
      current_group_id: session.group.id.toString(),
      email: session.user.email,
      is_curalate_user: session.internalInfo.isCuralateEmployee,
    });
    this.identifyCalled = true;
  }
}

const logger = new Logger();
export default logger;
